import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, type Item, type OutfitRow } from '../api.js';
import {
  Button, Card, Empty, ErrorBox, Loading, Screen, categoryLabel, slotLabel,
} from '../ui.js';

/**
 * Feste Kombinationen: „Kundentermin-Outfit" einmal zusammenstellen, benennen, speichern.
 *
 * Bis hierher gab es die Endpunkte, aber keinen Weg dorthin — die Tabelle blieb
 * dauerhaft leer, und damit auch der Bonus für gespeicherte Outfits in der Bewertung
 * (§3.3) sowie das Kennzeichen „gespeichertes Outfit" in der Morgens-Ansicht.
 *
 * Bewusst nur feste Teile, keine Regel-Slots: Ein Outfit, das erst zur Laufzeit
 * entscheidet, welches Hemd es meint, ist keine feste Kombination mehr. Regel-Slots kann
 * die Engine (`mode: 'rule'`), aber sie gehören nicht in diese Ansicht.
 */
export function Outfits({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const qc = useQueryClient();
  const [anlegen, setAnlegen] = useState(false);

  const outfits = useQuery({ queryKey: ['outfits'], queryFn: api.outfits.list });
  const items = useQuery({ queryKey: ['items'], queryFn: () => api.items({}) });

  const loeschen = useMutation({
    mutationFn: (id: string) => api.outfits.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['outfits'] });
      void qc.invalidateQueries({ queryKey: ['recommend'] });
    },
  });

  if (outfits.isLoading) return <Loading />;
  const liste = outfits.data ?? [];
  const nachId = new Map((items.data ?? []).map((i) => [i.id, i]));

  return (
    <Screen
      title="Outfits"
      subtitle="Feste Kombinationen, die als Ganzes vorgeschlagen werden"
      action={
        <Button variant="ghost" onClick={() => navigate('/mehr')}>
          Zurück
        </Button>
      }
    >
      <ErrorBox error={outfits.error ?? loeschen.error} />

      {anlegen ? (
        <NeuesOutfit
          items={items.data ?? []}
          onFertig={() => {
            setAnlegen(false);
            void qc.invalidateQueries({ queryKey: ['outfits'] });
            void qc.invalidateQueries({ queryKey: ['recommend'] });
          }}
          onAbbrechen={() => setAnlegen(false)}
        />
      ) : (
        <Button className="mb-4 w-full" onClick={() => setAnlegen(true)}>
          Outfit zusammenstellen
        </Button>
      )}

      {liste.length === 0 && !anlegen && (
        <Empty>
          Noch keine festen Kombinationen. Ein gespeichertes Outfit wird als Ganzes
          vorgeschlagen und bekommt einen kleinen Bonus — du hast es schließlich bewusst
          angelegt.
        </Empty>
      )}

      <div className="space-y-3">
        {liste.map((outfit) => (
          <OutfitKarte
            key={outfit.id}
            outfit={outfit}
            nachId={nachId}
            busy={loeschen.isPending}
            onLoeschen={() => loeschen.mutate(outfit.id)}
          />
        ))}
      </div>
      <div className="h-8" />
    </Screen>
  );
}

function OutfitKarte({ outfit, nachId, busy, onLoeschen }: {
  outfit: OutfitRow;
  nachId: Map<string, Item>;
  busy: boolean;
  onLoeschen: () => void;
}): ReactNode {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold">{outfit.name}</p>
          {outfit.formality_target !== null && (
            <p className="mt-0.5 text-[12px] text-white/45">
              Zielformalität {outfit.formality_target}
            </p>
          )}
        </div>
        <Button variant="ghost" disabled={busy} onClick={onLoeschen}>
          Entfernen
        </Button>
      </div>

      <ul className="mt-3 space-y-1.5">
        {outfit.slots.map((s) => {
          const teil = s.item_id === null ? undefined : nachId.get(s.item_id);
          return (
            <li key={s.id} className="flex items-center gap-2.5 text-[14px]">
              <span className="size-1.5 shrink-0 rounded-full bg-white/25" aria-hidden />
              {/* Ein Teil, das es nicht mehr gibt, wird benannt statt verschwiegen. */}
              <span className="truncate">{teil?.name ?? 'nicht mehr im Schrank'}</span>
              <span className="ml-auto shrink-0 text-[11px] text-white/30">
                {slotLabel(s.slot)}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** Reihenfolge der Slots wie beim Anziehen, nicht wie im Datenmodell. */
const SLOT_REIHENFOLGE = ['top', 'mid', 'outer', 'bottom', 'footwear', 'belt', 'accessory'];

function NeuesOutfit({ items, onFertig, onAbbrechen }: {
  items: Item[];
  onFertig: () => void;
  onAbbrechen: () => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Map<string, string>>(new Map());
  const [suche, setSuche] = useState('');

  const speichern = useMutation({
    mutationFn: () =>
      api.outfits.create({
        name: name.trim(),
        slots: [...gewaehlt.entries()].map(([slot, itemId], index) => ({
          slot,
          position: index,
          mode: 'fixed',
          itemId,
          // Pflicht sind Oberteil, Hose und Schuhe (§3.3) — alles andere darf fehlen,
          // etwa der Mantel an einem warmen Tag.
          optional: !['top', 'bottom', 'footwear'].includes(slot),
        })),
      }),
    onSuccess: onFertig,
  });

  const passend = items.filter(
    (i) =>
      suche === '' ||
      `${i.name} ${categoryLabel(i.category)}`.toLowerCase().includes(suche.toLowerCase()),
  );

  return (
    <Card className="mb-4">
      <p className="text-[15px] font-semibold">Neues Outfit</p>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name, z. B. „Kundentermin dunkel“"
        className="mt-3 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
      />

      <p className="mt-4 text-[12px] text-white/45">
        Je Slot ein Teil. Ein zweites Antippen nimmt es wieder heraus.
      </p>

      {gewaehlt.size > 0 && (
        <ul className="mt-2 space-y-1">
          {[...gewaehlt.entries()]
            .sort((a, b) => SLOT_REIHENFOLGE.indexOf(a[0]) - SLOT_REIHENFOLGE.indexOf(b[0]))
            .map(([slot, itemId]) => (
              <li key={slot} className="flex items-center gap-2 text-[14px]">
                <span className="text-white/40">{slotLabel(slot)}:</span>
                <span className="truncate">{items.find((i) => i.id === itemId)?.name}</span>
              </li>
            ))}
        </ul>
      )}

      <input
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder="Teil suchen …"
        className="mt-3 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
      />

      <div className="mt-2 max-h-72 space-y-1 overflow-y-auto">
        {passend.map((i) => {
          const slot = i.layer === 'base' ? 'top' : i.layer;
          const an = gewaehlt.get(slot) === i.id;
          return (
            <button
              key={i.id}
              onClick={() => {
                const next = new Map(gewaehlt);
                if (an) next.delete(slot);
                else next.set(slot, i.id);
                setGewaehlt(next);
              }}
              className={`press flex w-full items-center justify-between rounded-[var(--radius-control)] px-3 py-2.5 text-left text-[14px] ${
                an ? 'bg-[var(--color-accent-soft)] text-white' : 'bg-white/6 text-white/70'
              }`}
            >
              <span className="truncate">
                {i.name}
                <span className="ml-2 text-[11px] text-white/35">{slotLabel(slot)}</span>
              </span>
              {an && <span className="ml-2 shrink-0 text-[var(--color-accent)]">✓</span>}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          className="flex-1"
          disabled={name.trim() === '' || gewaehlt.size === 0 || speichern.isPending}
          onClick={() => speichern.mutate()}
        >
          Speichern
        </Button>
        <Button variant="ghost" onClick={onAbbrechen}>
          Abbrechen
        </Button>
      </div>
      <ErrorBox error={speichern.error} />
    </Card>
  );
}
